<template>
  <div>
    <TopMenu :title="$t('Playlist Details')">
      <template slot="right">
        <span>
          <a href="#" @click="displayOptionsToggle($event)"><FontAwesomeIcon icon="cog" /></a>
          <span @click="$event.stopPropagation()" class="top_right_options" v-if="displayOptions">
            <a v-if="playlist.user === userEmail" href="#" v-on:click="goToEditPlaylist($event)">
              <FontAwesomeIcon :icon="['far', 'edit']" class="ml-1" /> {{$t('Edit')}}
            </a>
            <a href="#" v-on:click="availableOfflineDialog($event)"><FontAwesomeIcon :icon="['far', 'arrow-alt-circle-down']" class="ml-1" /> {{$t('Set as offline')}}</a>
            <a href="#" v-on:click="duplicate($event)"><FontAwesomeIcon :icon="['far', 'copy']" class="ml-1" /> {{$t('Duplicate')}}</a>
            <a v-if="playlist.user === userEmail" href="#" v-on:click="deleteDialog($event)"><FontAwesomeIcon :icon="['far', 'trash-alt']" class="ml-1" /> {{$t('Delete')}}</a>
          </span>
        </span>
      </template>
    </TopMenu>
    <div class="view" @click="displayOptionsListener($event)" v-on:scroll="scrollHandler($event)">
      <DesktopBackLink :to="ROUTE_PLAYLISTS" :label="$t('Playlists')" />
      <transition name="fade" appear>
        <div style="overflow: hidden">
          <Loader :display="loadingPlaylistDetails" />
          <div :style="{ display: !loadingPlaylistDetails ? 'block' : 'none' }" class="p-4">
            <div class="d-flex align-items-start">
              <SongCover
                :coverUrl="playlist.cover_url"
                :alt="playlist.name + ' cover'"
                :size="80"
                class="mr-4"
              />
              <div>
                <h4 class="font-weight-light">{{playlist.name}}</h4>
                <small class="text-muted">{{ playlist.num_of_songs }} {{$t('songs')}} - {{ playlist.durationFormatted }}</small>
                <br>
                <small class="text-muted">
                  <span>
                    <span class="playlist-icon" :class="playlist.public ? 'text-primary' : 'text-muted'">
                      <FontAwesomeIcon icon="users" class="ml-1" />
                    </span>
                    {{$t('Public')}}
                  </span>
                  <span>
                    <span :class="isOfflineList ? 'text-primary' : 'text-muted'">
                      <FontAwesomeIcon :icon="['far', 'arrow-alt-circle-down']" class="ml-1" />
                    </span>
                    {{$t('Available Offline')}}
                  </span>
                  <span>
                    <span :class="playlist.shuffle ? 'text-primary' : 'text-muted'">
                      <FontAwesomeIcon icon="random" class="ml-1" />
                    </span>
                    {{$t('Shuffle')}}
                  </span>
                </small>
              </div>
            </div>
          </div>
          <div :style="{ display: !loadingPlaylistDetails ? 'block' : 'none' }" class="p-4">{{$t('Song List')}}</div>
          <SongsList :songs="playlistSongs" />
          <Modal
            :displayModal="displayDeleteModal"
            :onClose="() => { displayDeleteModal = false }"
            :onConfirm="confirmDelete"
            :body="$t('Delete playlist') + ' ?'"
          ></Modal>
          <Modal
            :displayModal="displayAvailableOfflineModal"
            :onClose="() => { displayAvailableOfflineModal = false }"
            :onConfirm="confirmAvailableOffline"
            :body="$t('Make available offline') + ' ?'"
          ></Modal>
        </div>
      </transition>
      <div :style="{ display: !loadingPlaylistDetails ? 'block' : 'none' }" @click="setBoxPlaylist()" class="footer clickable text-primary text-center pointer">
        <small :style="{ display: (updatingBoxPlaylist ? 'inline-block' : 'none') }"><FontAwesomeIcon icon="spinner" spin /></small>
        <small v-if="!updatingBoxPlaylist">{{$t('Switch to this list')}}</small>
      </div>
      <OverlayLoader :displayOverlay="deletingPlaylist || settingPlaylistAvailableOffline" />
    </div>
  </div>
</template>

<script>
import musicboxClient from '@/utils/musicboxClient'
import { ROUTE_PLAYLIST_CREATE, ROUTE_PLAYLIST_EDIT, ROUTE_PLAYLISTS, ROUTE_DASHBOARD } from '@/const/routeNames'
import { secondsToMinAndSec, secondsToHM } from '@/utils/numberUtils'
import scrollHandler from '@/utils/scrollHandler'
import localStorageUtil from '@/utils/localStorageUtil'
import audioManager from '@/utils/audioManager'
import SongCover from '@/components/shared/SongCover.vue'
import DesktopBackLink from '@/components/shared/DesktopBackLink.vue'

export default {

  name: 'Playlist',

  components: { SongCover, DesktopBackLink },

  mounted () {
    this.loadingPlaylistDetails = true
    musicboxClient
      .getPlaylist(this.$route.params.id)
      .then(res => {
        res.data.durationFormatted = secondsToHM(res.data.duration)
        res.data.songs.forEach(song => {
          song.minutesAndSeconds = secondsToMinAndSec(song.duration)
        })
        if (res.data.shuffle === true) {
          res.data.songs = res.data.songs.sort(() => Math.random() - 0.5)
        }
        this.playlist = res.data
      })
      .catch(e => {
        this.$notify.danger(this.$t('Failed to get playlist data'))
      })
      .then(() => { this.loadingPlaylistDetails = false })
  },

  data () {
    return {
      ROUTE_PLAYLIST_EDIT,
      ROUTE_PLAYLISTS,
      playlist: {},
      settingPlaylistAvailableOffline: false,
      deletingPlaylist: false,
      updatingBoxPlaylist: false,
      loadingPlaylistDetails: false,
      displayOptions: false,
      displayLimit: 30,
      displayDeleteModal: false,
      displayAvailableOfflineModal: false,
      optionsListenerSet: false
    }
  },

  computed: {
    playlistSongs () {
      return this.playlist.songs ? this.playlist.songs.slice(0, this.displayLimit) : []
    },
    userEmail () {
      return this.$store.state.auth.userEmail
    },
    isOfflineList () {
      return this.$store.state.audio.currentBox &&
        this.$store.state.audio.currentBox.offline_list &&
        this.$store.state.audio.currentBox.offline_list.id === parseInt(this.$route.params.id)
    }
  },

  methods: {
    scrollHandler: scrollHandler('playlist.songs'),
    duplicate (e) {
      e.preventDefault()
      this.$store.commit('clearEditedPlaylist')
      this.$store.commit('initEditedPlaylist')
      this.$store.commit('updateEditedPlaylist', { key: 'songList', value: this.playlist.songs })
      this.$router.push(ROUTE_PLAYLIST_CREATE)
    },
    confirmAvailableOffline () {
      this.displayAvailableOfflineModal = false
      this.settingPlaylistAvailableOffline = true
      musicboxClient
        .setOfflineList(this.$store.state.audio.currentBox.uuid, parseInt(this.$route.params.id))
        .then(res => {
          this.$notify.success(this.$t('Playlist now available offline'))
          localStorageUtil.setCurrentBox(res.data)
          this.$store.commit('setCurrentBox', res.data)
        })
        .catch(e => {
          this.$notify.danger(this.$t('Failed to set playlist available offline'))
        })
        .then(() => { this.settingPlaylistAvailableOffline = false })
    },
    confirmDelete () {
      this.displayDeleteModal = false
      this.deletingPlaylist = true
      musicboxClient
        .deletePlaylist(this.$route.params.id)
        .then(res => {
          this.$notify.success(this.$t('Playlist deleted'))
          this.$router.push(ROUTE_PLAYLISTS)
        })
        .catch(() => {
          this.$notify.danger(this.$t('Playlist delete failed'))
        })
        .then(() => { this.deletingPlaylist = false })
    },
    availableOfflineDialog (e) {
      e.preventDefault()
      this.displayOptions = false
      this.displayAvailableOfflineModal = true
    },
    deleteDialog (e) {
      e.preventDefault()
      this.displayOptions = false
      this.displayDeleteModal = true
    },
    goToEditPlaylist (e) {
      e.preventDefault()
      this.$store.commit('clearEditedPlaylist')
      this.$router.push(ROUTE_PLAYLIST_EDIT.replace(':id', this.$route.params.id))
    },
    displayOptionsListener (e) {
      this.displayOptions = false
    },
    displayOptionsToggle (e) {
      e.preventDefault()
      e.stopPropagation()
      this.displayOptions = !this.displayOptions
    },
    setBoxPlaylist () {
      if (this.$store.state.audio.currentBox && this.playlist.id && !this.updatingBoxPlaylist) {
        this.updatingBoxPlaylist = true
        const wasAudioPlaying = this.$store.state.audio.audioPlaying
        musicboxClient
          .setBoxPlaylist(this.$store.state.audio.currentBox.uuid, this.playlist.id)
          .then(res => {
            this.$notify.success(this.$t('Playlist switched'))
            audioManager.forceUpdateStatus()
            if (!wasAudioPlaying && this.$store.state.audio.currentBox.is_web) {
              setTimeout(() => { audioManager.play(this.$store.state.audio.currentBox.uuid) }, 512)
            }
            this.$router.push(ROUTE_DASHBOARD)
          })
          .catch(e => {
            this.$notify.danger(this.$t('Playlist switch failed'))
          })
          .then(() => { this.updatingBoxPlaylist = false })
        if (!wasAudioPlaying && this.$store.state.audio.currentBox.is_web) {
          audioManager.play(this.$store.state.audio.currentBox.uuid)
        }
      }
    }
  }
}

</script>

<style>
</style>



// WEBPACK FOOTER //
// src/components/Playlist.vue